// spec-060 — shared Google Places address field for the order forms.
// Sets only address/city/zip (Option A — never touches latitude/longitude).
import React from "react";
import { useFormikContext } from "formik";

const GOOGLE_KEY = "AIzaSyCkRYkFYLEOcaEbwmSYJXaQ2jlIttKkl8M";

let scriptPromise: Promise<void> | null = null;

export function loadPlaces(): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((window as any).google?.maps?.places) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_KEY}&libraries=places`;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => {
      scriptPromise = null; // allow a later retry
      reject(new Error("Failed to load Google Places"));
    };
    document.head.appendChild(s);
  });
  return scriptPromise;
}

export function AddressAutocomplete() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { values, setFieldValue, setFieldTouched } = useFormikContext<any>();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [verified, setVerified] = React.useState(false);

  React.useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let ac: any;
    loadPlaces()
      .then(() => {
        if (!inputRef.current) return;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const g = (window as any).google;
        ac = new g.maps.places.Autocomplete(inputRef.current, {
          types: ["address"],
          componentRestrictions: { country: "us" },
          fields: ["address_components", "geometry"],
        });
        ac.addListener("place_changed", () => {
          const place = ac.getPlace();
          if (!place || !place.geometry) return;
          let number = "";
          let street = "";
          let city = "";
          let zip = "";
          for (const c of place.address_components) {
            if (c.types.includes("street_number")) number = c.long_name;
            else if (c.types.includes("route")) street = c.long_name;
            else if (c.types.includes("locality")) city = c.long_name;
            else if (c.types.includes("sublocality") && !city) city = c.long_name;
            else if (c.types.includes("postal_code")) zip = c.long_name;
          }
          const zip5 = zip.match(/\d{5}/)?.[0] ?? zip;
          setFieldValue("address", `${number} ${street}`.trim());
          setFieldValue("city", city);
          setFieldValue("zip", zip5);
          // Option A: never touch latitude/longitude.
          setVerified(true);
        });
      })
      .catch(() => {
        /* Places unavailable — field still works as plain text. */
      });
    return () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const ev = (window as any).google?.maps?.event;
      if (ac && ev) ev.clearInstanceListeners(ac);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clear = () => {
    setVerified(false);
    setFieldValue("address", "");
    setFieldValue("city", "");
    setFieldValue("zip", "");
    // leave latitude/longitude untouched
    inputRef.current?.focus();
  };

  return (
    <div style={{ position: "relative" }}>
      <input
        ref={inputRef}
        id="address"
        name="address"
        className="text-input"
        autoComplete="off"
        placeholder="Start typing an address…"
        value={values.address || ""}
        readOnly={verified}
        onChange={(e) => setFieldValue("address", e.target.value)}
        onBlur={() => setFieldTouched("address", true)}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.preventDefault();
        }}
      />
      {verified ? (
        <div style={{ fontSize: 12, color: "#1a7f37", marginTop: 4 }}>
          ✓ Verified ·{" "}
          <span
            role="button"
            tabIndex={0}
            style={{ color: "#1f6feb", cursor: "pointer" }}
            onClick={clear}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") clear();
            }}
          >
            change
          </span>
        </div>
      ) : (
        <div style={{ fontSize: 12, color: "#656d76", marginTop: 4 }}>
          Pick an address from the dropdown to confirm it exists.
        </div>
      )}
    </div>
  );
}
